import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { AnchorLink, Breadcrumb, Icons, Alert } from '@shopline/dashboard-ui';
import Spacer from 'components/Spacer';
import styled from 'styled-components';
import { Grid, Page, PageTitle } from './components';
import StickyWrapper from 'components/StickyWrapper';
import useNav from './useNav';
import usePermissions from 'hooks/usePermissions';
import { useQueryMerchant } from './useMerchant';
import PARENT_E2E_PREFIX from './e2ePrefix';
import { useQueryInitialState } from 'hooks/useInitialState';
import ConfigurationForm from './ConfigurationForm';
import { Actions, useConfigurationForm } from 'contexts/ConfigurationForm';
import { useQueryRegionAndLocalesSets } from './useRegionAndLocaleSets';
import { redirectToPath } from './postMessages';
import {
  McaMerchantGoogleAccountForm,
  OauthMerchantGoogleAccountForm,
} from './MerchantGoogleAccountForm';
import {
  McaIntegrationForm,
  OauthIntegrationForm,
  OldIntegrationForm,
} from './IntegrationForm';
import { PRODUCT_API_TYPE, PRODUCT_API_AUTH_TYPE } from './constants';

const E2E_PREFIX = `${PARENT_E2E_PREFIX}-index`;

const googleMerchantCenterUrl = 'https://www.google.com/retail/';

const LinkContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 16px;

  a {
    color: ${({ theme }) => theme.colors.PRIMARY_DEFAULT};
    display: flex;
    align-items: center;
    font-weight: 600;
    font-size: 14px;
    line-height: 20px;
    letter-spacing: 0px;
    text-decoration: none;
    border-bottom: 1px solid ${({ theme }) => theme.colors.PRIMARY_400};

    svg {
      fill: ${({ theme }) => theme.colors.PRIMARY_DEFAULT};
    }
  }
`;

const BreadcrumbContainer = styled.div`
  margin-bottom: 16px;

  // breadcrumb item
  > div > span {
    &:hover {
      cursor: pointer;
    }
  }
`;

// 是否顯示舊版設定表單 (for Content API or 正要 migrate to Merchant API with oauth_token)
const shouldShowOldIntegrationForm = (
  isMerchantLoaded,
  merchant,
  gmcMcaEnabled,
  oauthIntegrationEnabled,
) => {
  if (!isMerchantLoaded) {
    return false;
  }

  // google_gmc_mca key 開啟時
  if (gmcMcaEnabled) {
    // productApiType 為 merchant_api，則不顯示舊版設定表單
    if (merchant?.productApiType === PRODUCT_API_TYPE.MERCHANT_API) {
      return false;
    }

    // 若沒有串接過 Content API，則不顯示舊版設定表單
    if (!merchant?.contentApiIntegrated) {
      return false;
    }
  }

  // google_merchant_api Key 開啟時，檢查 productApiType 是否為 merchant_api 且 productApiAuthType 是否為 oauth_token
  if (
    oauthIntegrationEnabled &&
    merchant?.productApiType === PRODUCT_API_TYPE.MERCHANT_API &&
    merchant?.productApiAuthType === PRODUCT_API_AUTH_TYPE.OAUTH_TOKEN
  ) {
    return false;
  }

  // 預設顯示舊版設定表單（未來 Content API 移除後可移除）
  return true;
};

// 是否顯示新版設定表單 (for MCA or OAuth Integration)
const shouldShowConfigurationForm = (
  isMerchantLoaded,
  merchant,
  gmcMcaEnabled,
  oauthIntegrationEnabled,
  hasMerchantGoogleAccount,
) => {
  if (!isMerchantLoaded) {
    return false;
  }

  // google_gmc_mca key 開啟時，檢查 productApiType 是否為 merchant_api
  if (
    gmcMcaEnabled &&
    merchant?.productApiType === PRODUCT_API_TYPE.MERCHANT_API
  ) {
    return true;
  }

  // google_merchant_api key 開啟時，檢查 productApiType 是否為 merchant_api 且 productApiAuthType 是否為 oauth_token
  // 若沒有授權中的 Google 帳號，代表他已經解串過（不顯示新版設定表單）
  if (
    oauthIntegrationEnabled &&
    merchant?.productApiType === PRODUCT_API_TYPE.MERCHANT_API &&
    merchant?.productApiAuthType === PRODUCT_API_AUTH_TYPE.OAUTH_TOKEN
  ) {
    return hasMerchantGoogleAccount;
  }
  return false;
};

const Main = () => {
  const { t } = useTranslation(['main', 'pageTitle']);
  const {
    items,
    refs: { formSectionRef, listSectionRef },
  } = useNav({ e2ePrefix: `${E2E_PREFIX}-use-nav` });

  const canQuery = usePermissions([
    ['merchant', 'show'],
    ['regionLocaleSet', 'index'],
  ]);
  const canEdit = usePermissions('merchant', 'edit');
  const { isSuccess: isMerchantLoaded, data: merchant } = useQueryMerchant();
  const { isSuccess: isInitialStateLoaded, data: initialState } =
    useQueryInitialState();
  const { isSuccess: isRegionAndLocaleSetsLoaded, data: regionAndLocaleSets } =
    useQueryRegionAndLocalesSets();
  const { dispatch } = useConfigurationForm();
  /* eslint-disable jsx-a11y/anchor-has-content */

  const gmcMcaEnabled =
    isInitialStateLoaded &&
    initialState?.merchant?.rollout_keys?.includes('google_gmc_mca');
  const oauthIntegrationEnabled =
    isInitialStateLoaded &&
    initialState?.merchant?.rollout_keys?.includes('google_merchant_api');
  const hasMerchantGoogleAccount =
    isInitialStateLoaded && initialState?.merchantGoogleAccount;
  const showOldIntegrationForm = shouldShowOldIntegrationForm(
    isMerchantLoaded,
    merchant,
    gmcMcaEnabled,
    oauthIntegrationEnabled,
  );
  const showSideAnchorLink =
    showOldIntegrationForm && !(gmcMcaEnabled || oauthIntegrationEnabled);
  const showConfigurationForm = shouldShowConfigurationForm(
    isMerchantLoaded,
    merchant,
    gmcMcaEnabled,
    oauthIntegrationEnabled,
    hasMerchantGoogleAccount,
  );
  const showAlertOfMCAAccess =
    gmcMcaEnabled && showConfigurationForm && !canEdit;

  const breadcrumbItems = [
    {
      id: 'apps-store',
      label: t('Apps Store'),
      onClick: () => redirectToPath('/apps/apps_store'),
    },
    {
      id: 'my-apps',
      label: t('My Apps'),
      onClick: () => redirectToPath('/apps/my_apps'),
    },
    {
      id: 'google-merchant-center',
      label: t('Google Merchant Center', { ns: 'pageTitle' }),
    },
  ];

  useEffect(() => {
    if (isMerchantLoaded && isRegionAndLocaleSetsLoaded) {
      dispatch(Actions.setCurrentState(merchant, regionAndLocaleSets));
    }
  }, [isMerchantLoaded, isRegionAndLocaleSetsLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Page
      titleLeftSection={
        <>
          <BreadcrumbContainer>
            <Breadcrumb items={breadcrumbItems} />
          </BreadcrumbContainer>
          <PageTitle>
            {t('Google Merchant Center', { ns: 'pageTitle' })}
            <Spacer x="0.25rem" />
            <a href={t('App support link')} target="_blank" rel="noreferrer">
              <Icons.Outline.QuestionMark size="MEDIUM" color="INK_400" />
            </a>
          </PageTitle>
        </>
      }
    >
      {canQuery && (
        <Grid.Row>
          <Grid.Column
            disableGutter
            lg={showSideAnchorLink ? 8 : 12}
            md={showSideAnchorLink ? 8 : 12}
            sd={12}
          >
            {showAlertOfMCAAccess && (
              <Alert.Information>
                {t(
                  'If you need to manage the Google Merchant Center account, please contact the account owner and request manual authorization in the Google Merchant Center dashboard.',
                )}
              </Alert.Information>
            )}
            {gmcMcaEnabled && (
              <>
                <McaMerchantGoogleAccountForm />
                {hasMerchantGoogleAccount && <McaIntegrationForm />}
              </>
            )}
            {oauthIntegrationEnabled && (
              <>
                <OauthMerchantGoogleAccountForm />
                {hasMerchantGoogleAccount && <OauthIntegrationForm />}
              </>
            )}
            {showConfigurationForm && <ConfigurationForm />}
            {showOldIntegrationForm && (
              <>
                {oauthIntegrationEnabled && (
                  <Alert.Warning>
                    {t(
                      'Google discontinued the Content API on 18 August 2026. Please upgrade to the Merchant API to keep syncing product data with your GMC account.',
                    )}
                  </Alert.Warning>
                )}
                <OldIntegrationForm
                  formSectionRef={formSectionRef}
                  listSectionRef={listSectionRef}
                  disabled={oauthIntegrationEnabled}
                />
              </>
            )}
            <LinkContainer>
              <a
                href={googleMerchantCenterUrl}
                target="_blank"
                rel="noreferrer noopener"
              >
                {t('Go to Google Merchant Center')}
                <Icons.Outline.Link size="MEDIUM" />
              </a>
            </LinkContainer>
          </Grid.Column>
          {showSideAnchorLink && (
            <Grid.Column disableGutter lg={4} md={4} sd={12}>
              <StickyWrapper top={20}>
                <AnchorLink
                  scrollbarWrapper={document.documentElement}
                  items={items}
                />
              </StickyWrapper>
            </Grid.Column>
          )}
        </Grid.Row>
      )}
    </Page>
  );
  /* eslint-enable jsx-a11y/anchor-has-content */
};

export default Main;
